import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, ready, verifying, success, error, already_verified
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  
  const token = searchParams.get('token');

  // Step 1: Check if token is valid (GET - doesn't verify)
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Lien de vérification invalide');
      return;
    }

    const checkToken = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/verify-email?token=${token}`);
        if (response.data.already_verified) {
          setStatus('already_verified');
          setMessage(response.data.message);
        } else if (response.data.valid) {
          // Token is valid, show confirmation button
          setStatus('ready');
          setEmail(response.data.email || '');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Une erreur est survenue lors de la vérification');
      }
    };

    checkToken();
  }, [token]);

  // Step 2: Actually verify when user clicks button (POST)
  const handleVerify = async () => {
    setStatus('verifying');
    try {
      const response = await axios.post(`${API}/api/auth/verify-email?token=${token}`);
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
      } else if (response.data.already_verified) {
        setStatus('already_verified');
        setMessage(response.data.message);
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Une erreur est survenue lors de la vérification');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="p-2 bg-emerald-500/20 rounded-xl">
            <svg className="w-8 h-8 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z"/>
            </svg>
          </div>
          <span className="text-emerald-500 font-bold text-2xl font-['Outfit']">YOUMI GREEN</span>
          <span className="text-white font-bold text-2xl font-['Outfit']">VTC</span>
        </div>

        {/* Status Card */}
        <div className="glass-card rounded-2xl p-8">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Vérification en cours...</h1>
              <p className="text-zinc-400">Veuillez patienter</p>
            </>
          )}

          {status === 'ready' && (
            <>
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-12 h-12 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Confirmer votre email</h1>
              {email && (
                <p className="text-emerald-400 font-medium mb-2">{email}</p>
              )}
              <p className="text-zinc-400 mb-6">
                Cliquez sur le bouton ci-dessous pour activer votre compte
              </p>
              <Button 
                onClick={handleVerify}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="confirm-email-btn"
              >
                <CheckCircle className="w-5 h-5" />
                Confirmer mon email
              </Button>
            </>
          )}

          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Activation en cours...</h1>
              <p className="text-zinc-400">Veuillez patienter</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Email vérifié !</h1>
              <p className="text-zinc-400 mb-6">{message}</p>
              <Button 
                onClick={() => navigate('/auth')}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="go-to-login-btn"
              >
                Se connecter
                <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}

          {status === 'already_verified' && (
            <>
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-12 h-12 text-blue-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Déjà vérifié</h1>
              <p className="text-zinc-400 mb-6">{message}</p>
              <Button 
                onClick={() => navigate('/auth')}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                Se connecter
                <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Erreur de vérification</h1>
              <p className="text-zinc-400 mb-6">{message}</p>
              <Link to="/">
                <Button variant="outline" className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800">
                  Retour à l'accueil
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-zinc-500 text-sm mt-8">
          YOUMI GREEN VTC - Service VTC 100% électrique à Nice
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
